(() => {
  'use strict';

  const CARD_SELECTOR = '.lolly-product-card';

  /*
    BSS has TWO relevant runtime structures:

    1) Product-name badge wrapper:
       .bss_pb_img

    2) Dedicated stock component:
       .bss-pb-stock-wrapper
         └── .bss-pb-stock
             └── .bss-pb-stock-message

    Shopify/theme product.available is the only source of truth for
    availability on Lollystore product cards.
  */
  const BSS_BADGE_WRAPPER_SELECTOR = '.bss_pb_img';
  const BSS_STOCK_WRAPPER_SELECTOR = '.bss-pb-stock-wrapper';
  const BSS_STOCK_INNER_SELECTOR = '.bss-pb-stock, .bss-pb-stock-message';

  const STOCK_STATUS_TEXTS = [
    'slut i lager',
    'tillfälligt slut',
    'sold out',
    'out of stock'
  ];

  const normalize = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLocaleLowerCase('sv-SE');

  const elementText = (element) => {
    const parts = [element.textContent || ''];

    element.querySelectorAll?.('[alt], [title], [aria-label]').forEach((child) => {
      parts.push(
        child.getAttribute('alt') || '',
        child.getAttribute('title') || '',
        child.getAttribute('aria-label') || ''
      );
    });

    return normalize(parts.join(' '));
  };

  const containsStockStatusText = (element) => {
    const text = elementText(element);
    return STOCK_STATUS_TEXTS.some((needle) => text.includes(needle));
  };

  /*
    BSS may inject its UI:
    - inside a product card
    - as a sibling of a node inside/next to a product card

    We only remove stock UI when it can be associated with a Lollystore
    product card. This avoids touching BSS elsewhere on the storefront.
  */
  const findRelatedProductCard = (element) => {
    if (!(element instanceof Element)) return null;

    const directCard = element.closest(CARD_SELECTOR);
    if (directCard) return directCard;

    const parent = element.parentElement;
    if (!parent) return null;

    if (parent.matches(CARD_SELECTOR)) return parent;

    const parentCard = parent.closest(CARD_SELECTOR);
    if (parentCard) return parentCard;

    const candidates = [
      element.previousElementSibling,
      element.nextElementSibling,
      parent.previousElementSibling,
      parent.nextElementSibling
    ].filter(Boolean);

    for (const candidate of candidates) {
      if (candidate.matches?.(CARD_SELECTOR)) return candidate;

      const nestedCard = candidate.querySelector?.(CARD_SELECTOR);
      if (nestedCard) return nestedCard;

      const ancestorCard = candidate.closest?.(CARD_SELECTOR);
      if (ancestorCard) return ancestorCard;
    }

    return null;
  };

  const removeDedicatedBssStockComponent = (element) => {
    if (!(element instanceof Element)) return false;

    const wrapper = element.matches(BSS_STOCK_WRAPPER_SELECTOR)
      ? element
      : element.closest(BSS_STOCK_WRAPPER_SELECTOR);

    if (!wrapper) return false;
    if (!findRelatedProductCard(wrapper)) return false;

    /*
      This component is BSS's dedicated inventory-status UI.
      It is removed from product cards regardless of its current text because
      BSS is not allowed to override Shopify/theme availability state.
    */
    wrapper.remove();
    return true;
  };

  const removeLegacyBssStockBadge = (wrapper) => {
    if (!(wrapper instanceof Element)) return;
    if (!wrapper.matches(BSS_BADGE_WRAPPER_SELECTOR)) return;
    if (!findRelatedProductCard(wrapper)) return;

    const hasStockClass = Boolean(wrapper.querySelector(BSS_STOCK_INNER_SELECTOR));
    const hasStockText = containsStockStatusText(wrapper);

    if (hasStockClass || hasStockText) {
      wrapper.remove();
    }
  };

  const inspectNode = (node) => {
    if (!(node instanceof Element)) return;

    /*
      First remove the dedicated stock component. If the added node is inside
      that component, closest() finds the wrapper and removes it.
    */
    removeDedicatedBssStockComponent(node);

    if (node.matches(BSS_BADGE_WRAPPER_SELECTOR)) {
      removeLegacyBssStockBadge(node);
    }

    const containingBadge = node.closest(BSS_BADGE_WRAPPER_SELECTOR);
    if (containingBadge) {
      removeLegacyBssStockBadge(containingBadge);
    }

    node.querySelectorAll?.(BSS_STOCK_WRAPPER_SELECTOR).forEach((wrapper) => {
      removeDedicatedBssStockComponent(wrapper);
    });

    node.querySelectorAll?.(BSS_BADGE_WRAPPER_SELECTOR).forEach((wrapper) => {
      removeLegacyBssStockBadge(wrapper);
    });
  };

  const cleanup = (root = document) => {
    root.querySelectorAll?.(BSS_STOCK_WRAPPER_SELECTOR).forEach((wrapper) => {
      removeDedicatedBssStockComponent(wrapper);
    });

    root.querySelectorAll?.(BSS_BADGE_WRAPPER_SELECTOR).forEach((wrapper) => {
      removeLegacyBssStockBadge(wrapper);
    });
  };

  const startObserver = () => {
    if (!document.body) return;

    cleanup();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(inspectNode);
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    document.addEventListener('shopify:section:load', (event) => {
      cleanup(event.target);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserver, { once: true });
  } else {
    startObserver();
  }
})();
